import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { IonicModule, IonPopover } from '@ionic/angular';
import { TranslatePipe } from '@ngx-translate/core';
import { MarkdownEditorService } from '../../services/markdown-editor.service';
import {
  APPLY_LINK_COMMAND,
  CLOSE_LINK_DIALOG_COMMAND,
  LinkDialogPlugin,
  registerLinkDialog
} from '../../plugins/link-dialog.plugin';
import { Subject, takeUntil } from 'rxjs';
import { LexicalEditor } from 'lexical';

@Component({
  selector: 'bl-editor-link-popover',
  standalone: true,
  imports: [CommonModule, IonicModule, FormsModule, TranslatePipe],
  styles: [
    `
      .link-popover {
        --width: min(90vw, 420px);
        --max-width: 90vw;
      }

      ion-input {
        --padding-start: 8px;
        --padding-end: 8px;
      }
    `
  ],
  template: `
    <ion-popover
      [isOpen]="isOpen"
      [dismissOnSelect]="false"
      [size]="'auto'"
      [cssClass]="'link-popover'"
      (ionPopoverDidDismiss)="handleDismiss()"
      (didDismiss)="handleDismiss()"
    >
      <ng-template>
        <ion-content class="ion-padding">
          <ion-input
            [(ngModel)]="url"
            placeholder="https://"
            type="url"
            class="link-input"
            autocapitalize="off"
            autocorrect="off"
            inputmode="url"
          ></ion-input>
        </ion-content>
        <ion-footer class="ion-no-border ion-padding-horizontal">
          <ion-toolbar
            class="pb-4"
            mode="ios"
          >
            <ion-button
              (click)="onApply()"
              expand="block"
              class="text--base"
            >
              {{ 'COMMON.LABELS.APPLY' | translate }}
            </ion-button>
            <ion-button
              (click)="onCancel()"
              expand="block"
              fill="outline"
              class="ion-margin-top text--base"
            >
              {{ 'COMMON.LABELS.CANCEL' | translate }}
            </ion-button>
            <ion-button
              *ngIf="isExistingLink"
              (click)="onRemove()"
              expand="block"
              fill="outline"
              color="danger"
              class="ion-margin-top text--base"
            >
              {{ 'COMMON.LABELS.REMOVE' | translate }}
            </ion-button>
          </ion-toolbar>
        </ion-footer>
      </ng-template>
    </ion-popover>
  `
})
export class EditorLinkPopoverComponent implements OnInit, OnDestroy {
  private ngUnsubscribe = new Subject<void>();
  private editor!: LexicalEditor;
  private linkDialogPlugin!: LinkDialogPlugin;

  @ViewChild(IonPopover) popover!: IonPopover;

  isOpen = false;
  url = 'https://';

  constructor(private editorService: MarkdownEditorService) {}

  get isExistingLink(): boolean {
    return this.url !== 'https://';
  }

  ngOnInit(): void {
    this.editor = this.editorService.getEditor();
    this.linkDialogPlugin = registerLinkDialog(this.editor);

    this.linkDialogPlugin.dialogState$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(state => {
        this.isOpen = state.isOpen;
        if (state.isOpen) {
          this.url = state.url;
        }
      });
  }

  ngOnDestroy() {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.linkDialogPlugin.unregister();
  }

  handleDismiss(): void {
    this.isOpen = false;
    this.onCancel();
  }

  onCancel(): void {
    this.editor.dispatchCommand(CLOSE_LINK_DIALOG_COMMAND, undefined);
  }

  onApply(): void {
    this.editor.dispatchCommand(APPLY_LINK_COMMAND, this.url);
  }

  onRemove(): void {
    this.editor.dispatchCommand(APPLY_LINK_COMMAND, null);
  }
}
